'use client';

import { useLocale } from 'next-intl';
import { usePathname as useRawPathname } from 'next/navigation';
import { routing } from '~/i18n/routing';

/**
 * Display labels per locale code.
 * `ka` is ISO-639-1 for Georgian (Kartuli/ქართული) — but visitors expect a country-style code,
 * so we render `GEO` to match `EN`. The internal locale slug stays `ka` everywhere else
 * (URL `/ka`, cookie `NEXT_LOCALE=ka`, messages/ka.json) — only the label differs.
 */
const LOCALE_LABELS: Record<string, string> = {
    en: 'EN',
    ka: 'GEO',
};


/**
 * Переключатель EN ↔ KA в правом углу top-bar хедера.
 * Click на «EN» когда текущая KA → меняет URL `/ka/foo` → `/en/foo`.
 * pending-state в transition чтобы не моргать UI пока Next.js перенаправляет.
 */
export function LocaleSwitcher() {
    const rawPathname = useRawPathname();
    const _ = useLocale(); // keep next-intl provider awareness for SSR consistency
    const isPending = false;

    // Derive current locale from the LIVE URL — single source of truth.
    const currentLocale: (typeof routing.locales)[number] =
        (routing.locales as readonly string[]).find(l => rawPathname.startsWith(`/${l}`))
            ? (rawPathname.split('/')[1] as (typeof routing.locales)[number])
            : routing.defaultLocale;

    const strippedPath = rawPathname.replace(/^\/(en|ka)(?=\/|$)/, '') || '/';

    /**
     * Hard navigation via window.location.href.
     *
     * Why not router.replace from next-intl? Client-side navigation updates URL +
     * cookie but doesn't tear down the React tree. Stale state survives in:
     *   - <html lang> attribute (server-set, not updated on client nav)
     *   - jotai atoms (sidebar open state, sex selector)
     *   - tRPC react-query cache (categories fetched in prior locale context)
     *   - NextIntlClientProvider inside react-aria-components Portal subtree
     *
     * Hard nav forces a full page rebuild. 100% correctness, ~500ms reload.
     * The user still doesn't press F5 — the button does.
     */
    const switchTo = (next: (typeof routing.locales)[number]) => {
        if (next === currentLocale) return;
        const target = `/${next}${strippedPath === '/' ? '' : strippedPath}`;
        window.location.href = target;
    };

    return (
        <span className={'inline-flex items-center gap-x-1.5 text-12'} aria-busy={isPending}>
            {routing.locales.map((locale, idx) => (
                <span key={locale} className={'inline-flex items-center'}>
                    <button
                        type={'button'}
                        onClick={() => switchTo(locale)}
                        className={
                            locale === currentLocale
                                ? 'font-700 underline'
                                : 'opacity-60 hover:opacity-100 focus-visible:underline'
                        }
                        aria-current={locale === currentLocale ? 'true' : undefined}
                    >
                        {LOCALE_LABELS[locale] ?? locale.toUpperCase()}
                    </button>
                    {idx < routing.locales.length - 1 && <span className={'px-1 text-neutral-400'}>·</span>}
                </span>
            ))}
        </span>
    );
}
